/**
 * `/connect-agent?request=<id>` — pantalla de consentimiento para conectar un
 * agente de IA (Claude Code, Claude.ai, ChatGPT…) al servidor MCP de HIVA
 * Nexus (OT-0043, Docs/15 §3.1).
 *
 * El servidor de autorización del MCP manda aquí el navegador. El usuario
 * inicia sesión con su cuenta normal (MSAL regresa a esta misma URL), ve qué
 * cliente pide acceso y su dominio verificado, y aprueba o cancela. La API
 * devuelve la URL de retorno al cliente, a la que se navega fuera de la app.
 *
 * Layout enfocado (`staticData.layout`): sin sidebar ni topbar.
 */

import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ShieldCheck, X } from 'lucide-react'

import {
  approveConnectionRequest,
  denyConnectionRequest,
  getConnectionRequest,
  type ConnectionRequest,
} from '../api/agentConnections'
import { LoginScreen } from '../auth/LoginScreen'
import { useAccessToken } from '../auth/useAccessToken'
import { useMe } from '../auth/useMe'
import { canConnectAgent } from '../lib/tiers'
import { useApiErrorMessage } from '../hooks/useApiErrorMessage'
import { BrandMark } from '../components/shell/BrandMark'

export function ConnectAgentView() {
  const { t } = useTranslation('agents')
  const { request } = useSearch({ from: '/connect-agent' })

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen subtitle={t('connect.sign_in_subtitle')} />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <div className="h-full overflow-y-auto grid place-items-center px-6 py-16">
          <section className="w-full max-w-lg rounded-lg border border-line bg-surface p-8 shadow-sm">
            <BrandMark className="h-8 w-8 text-brand" />
            {request ? (
              <ConnectionRequestPanel requestId={request} />
            ) : (
              <Notice text={t('connect.missing_request')} />
            )}
          </section>
        </div>
      </AuthenticatedTemplate>
    </>
  )
}

function ConnectionRequestPanel({ requestId }: { requestId: string }) {
  const { me, isLoading: meLoading, error: meError } = useMe()
  // "Permitir" da acceso nuevo: en solo lectura el backend lo niega y no se
  // ofrece (OT-0073 §4.4). "Cancelar" no se condiciona: el backend deja a
  // cualquier usuario activo rechazar su propia solicitud, también en solo
  // lectura (`READ_ONLY_SAFE_ROUTES`) y sin acceso a agentes, para volver
  // al cliente.
  const canApprove = canConnectAgent(me?.role, me?.tenant?.access)
  const { t } = useTranslation('agents')
  const { accessToken } = useAccessToken()
  const [returning, setReturning] = useState(false)
  const [allowAuthoring, setAllowAuthoring] = useState(false)

  const query = useQuery({
    queryKey: ['agent-connection-request', requestId],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getConnectionRequest(accessToken, requestId)
    },
    enabled: !!accessToken,
    retry: false,
    staleTime: Infinity,
  })

  const decide = useMutation({
    mutationFn: (approve: boolean) => {
      if (!accessToken) throw new Error('no_access_token')
      return approve
        ? approveConnectionRequest(accessToken, requestId, allowAuthoring)
        : denyConnectionRequest(accessToken, requestId)
    },
    onSuccess: ({ redirect_url }) => {
      setReturning(true)
      window.location.assign(redirect_url)
    },
  })

  // Sin `/me` no se sabe si se puede permitir: decidir antes mostraría por
  // un momento el aviso de solo lectura en una organización activa.
  if (query.isPending || meLoading) {
    return <Notice text={t('connect.loading')} />
  }
  if (query.isError) {
    return <ErrorNotice error={query.error} />
  }
  // Sin la cuenta tampoco se sabe si se puede permitir: se muestra el error de
  // carga, no el aviso de solo lectura (que con `me` vacío saldría siempre).
  if (meError) {
    return <ErrorNotice error={meError} />
  }

  const connection = query.data
  if (returning) {
    return <Notice text={t('connect.returning', { client: connection.client_name })} />
  }

  return (
    <div className="mt-5">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
        {t('connect.eyebrow')}
      </p>
      {connection.allowed ? (
        <AllowedRequest
          connection={connection}
          allowAuthoring={allowAuthoring}
          onAllowAuthoringChange={setAllowAuthoring}
        />
      ) : (
        <>
          <h1 className="mt-2 font-display text-2xl font-semibold text-ink">
            {t('connect.not_allowed_title')}
          </h1>
          <p className="mt-2 font-body text-sm text-ink-muted">
            {t('connect.not_allowed_body')}
          </p>
        </>
      )}

      {connection.allowed && !canApprove && (
        <p role="status" className="mt-4 rounded-md bg-review-soft px-3 py-2 text-sm text-review">
          {t('shell:organization.read_only_action')}
        </p>
      )}

      {decide.isError && <ErrorNotice error={decide.error} />}

      <div className="mt-8 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => decide.mutate(false)}
          disabled={decide.isPending}
          className="rounded-md border border-line bg-surface px-4 py-2.5 font-ui text-sm text-ink-muted
                     hover:text-ink disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand/40"
        >
          {t('connect.cancel')}
        </button>
        {connection.allowed && canApprove && (
          <button
            type="button"
            onClick={() => decide.mutate(true)}
            disabled={decide.isPending}
            className="rounded-md bg-brand px-5 py-2.5 font-ui text-sm text-canvas transition-colors
                       hover:bg-brand-deep disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {t('connect.allow')}
          </button>
        )}
      </div>
    </div>
  )
}

function AllowedRequest({
  connection,
  allowAuthoring,
  onAllowAuthoringChange,
}: {
  connection: ConnectionRequest
  allowAuthoring: boolean
  onAllowAuthoringChange: (allow: boolean) => void
}) {
  const { t } = useTranslation('agents')
  const client = connection.client_name
  // Lo que verá el agente depende de la casilla de autoría (solo admins).
  const authoring = connection.can_author && allowAuthoring
  return (
    <>
      <h1 className="mt-2 font-display text-2xl font-semibold text-ink">
        {t('connect.title', { client })}
      </h1>
      <p className="mt-2 font-body text-sm text-ink-muted">{t('connect.subtitle', { client })}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-official-soft px-2 py-0.5 text-[11px] font-medium text-official">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t('connect.verified_domain', { domain: connection.client_domain })}
        </span>
        <span className="inline-flex items-center rounded-md bg-surface-muted px-2 py-0.5 font-mono text-[11px] text-ink-muted">
          {t('connect.redirect_to', { host: connection.redirect_host })}
        </span>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <CapabilityList
          title={t('connect.can')}
          items={[
            t('connect.can_items.list_projects'),
            t('connect.can_items.read_official'),
            ...(authoring ? [t('connect.can_items.author_drafts')] : []),
          ]}
          allowed
        />
        <CapabilityList
          title={t('connect.cannot')}
          items={[
            t('connect.cannot_items.drafts'),
            authoring ? t('connect.cannot_items.publish') : t('connect.cannot_items.modify'),
          ]}
          allowed={false}
        />
      </div>

      {connection.can_author && (
        <label className="mt-5 flex items-start gap-3 rounded-md border border-line bg-surface p-3">
          <input
            type="checkbox"
            checked={allowAuthoring}
            onChange={(e) => onAllowAuthoringChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-brand"
          />
          <span>
            <span className="block font-ui text-sm text-ink">{t('connect.authoring.label')}</span>
            <span className="mt-0.5 block font-body text-xs text-ink-muted">
              {t('connect.authoring.hint')}
            </span>
          </span>
        </label>
      )}

      <p className="mt-6 font-body text-xs text-ink-subtle">{t('connect.revoke_hint')}</p>
    </>
  )
}

function CapabilityList({
  title,
  items,
  allowed,
}: {
  title: string
  items: string[]
  allowed: boolean
}) {
  const Icon = allowed ? Check : X
  return (
    <div className="rounded-md border border-line p-4">
      <p className="font-ui text-xs font-semibold text-ink">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 font-body text-sm text-ink-muted">
            <Icon
              className={`mt-0.5 h-4 w-4 shrink-0 ${allowed ? 'text-official' : 'text-ink-subtle'}`}
              aria-hidden="true"
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Notice({ text }: { text: string }) {
  return <p className="mt-5 font-body text-sm text-ink-muted">{text}</p>
}

function ErrorNotice({ error }: { error: unknown }) {
  const message = useApiErrorMessage()(error)
  return (
    <p
      role="alert"
      className="mt-5 rounded-md border border-critical/40 bg-critical-soft p-4 font-body text-sm text-critical"
    >
      {message}
    </p>
  )
}
